import React from 'react';
import { Analytics } from '@vercel/analytics/react';
import '@/App.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider } from './context/AuthContext';
import { QuranProvider } from './context/QuranContext';
import { Toaster } from './components/ui/sonner';
import SurahSelector from './components/SurahSelector';
import TranslationSelector from './components/TranslationSelector';
import TranslationSettings from './components/TranslationSettings';
import AISearch from './components/AISearch';
import TopicManager from './components/TopicManager';
import HelpGuide from './components/HelpGuide';
import UserMenu from './components/UserMenu';
import VerseDisplay from './components/VerseDisplay';
import RootNavigator from './components/RootNavigator';
import StoryReader from './components/StoryReader';
import AdminDashboard from './components/AdminDashboard';
import LibraryPage from './components/LibraryPage';
import { BookOpen } from 'lucide-react';

const QuranApp = () => {
    const [settingsOpen, setSettingsOpen] = React.useState(false);
    return (
        <div className="min-h-screen bg-[#02040a] relative">
            {/* Background Texture */}
            <div className="bg-texture" />
            
            {/* Main Content */}
            <div className="relative z-10 pb-24">
                {/* Header - Two rows for mobile */}
                <header className="app-header py-3 px-3 sm:py-4 sm:px-4">
                    <div className="max-w-7xl mx-auto flex flex-col gap-2 sm:gap-3">
                        {/* Row 1: Logo, Title, Action Buttons */}
                        <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0" data-testid="app-header">
                                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-br from-[#d4af37] to-[#a16207] flex items-center justify-center">
                                    <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-[#02040a]" />
                                </div>
                                <h1 className="heading-primary text-lg sm:text-xl md:text-2xl text-[#f8fafc]">
                                    Ai Quran
                                </h1>
                            </div>
                            
                            <div className="flex items-center gap-2 sm:gap-3">
                                <AISearch />
                                <TopicManager />
                                <HelpGuide />
                                <UserMenu onOpenSettings={() => setSettingsOpen(true)} />
                            </div>
                        </div>

                        {/* Row 2: Surah, Verse, Translation Selectors */}
                        <div className="flex items-center gap-2 sm:gap-3">
                            <SurahSelector />
                            <TranslationSelector />
                        </div>
                    </div>
                </header>

                {/* Main Verse Display Area */}
                <main className="max-w-5xl mx-auto px-4 py-8">
                    <VerseDisplay />
                </main>

                {/* Footer */}
                <footer className="border-t border-[#1e293b] py-6 px-4 mt-12">
                    <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-center items-center gap-4 text-center">
                        <p className="text-[#475569] text-sm ui-text">
                            For feedback reach out to{' '}
                            <a href="mailto:info@aiquran.org" className="text-[#d4af37] hover:text-[#facc15] transition-colors underline">
                                info@aiquran.org
                            </a>
                        </p>
                        <span className="hidden md:inline text-[#1e293b]">•</span>
                        <a href="/library" className="text-[#475569] hover:text-[#f8fafc] text-sm transition-colors cursor-pointer">
                            Published Narratives
                        </a>
                    </div>
                </footer>
            </div>

            {/* Root Word Navigation Bar - Fixed at bottom when browsing */}
            <RootNavigator />

            {/* Translation Settings Sheet (Controlled) */}
            <TranslationSettings open={settingsOpen} onOpenChange={setSettingsOpen} />
        </div>
    );
};

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

function App() {
    return (
        <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
            <AuthProvider>
                <div className="App">
                    <QuranProvider>
                        <BrowserRouter>
                            <Routes>
                                <Route path="/" element={<QuranApp />} />
                                <Route path="/narrative/:topicId" element={<StoryReader />} />
                                <Route path="/library" element={<LibraryPage />} />
                                <Route path="/admin" element={<AdminDashboard />} />
                                <Route path="*" element={<QuranApp />} />
                            </Routes>
                        </BrowserRouter>
                        <Toaster 
                            position="bottom-right"
                            toastOptions={{
                                style: {
                                    background: '#09090b',
                                    border: '1px solid #1e293b',
                                    color: '#f8fafc'
                                }
                            }}
                        />
                    </QuranProvider>
                    <Analytics />
                </div>
            </AuthProvider>
        </GoogleOAuthProvider>
    );
}

export default App;
